import { formatMoney, formatNumber, formatPercent } from './formatMoney';

function sumField(rows, key) {
  return rows.reduce((sum, row) => sum + Number(row[key] || 0), 0);
}

function truncateLabel(text, max = 28) {
  if (!text) return '—';
  const trimmed = String(text).trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
}

function sharePercent(part, total) {
  if (!total || total <= 0) return null;
  return (Number(part || 0) / total) * 100;
}

function neutralStat(title, value, context) {
  return {
    title,
    value,
    change: '—',
    trend: 'neutral',
    context,
  };
}

export function buildProductSummaryStats(products, { currencyCode, currencyUnavailable }) {
  if (!Array.isArray(products) || products.length === 0) return [];

  const totalRevenue = sumField(products, 'totalRevenue');
  const totalUnits = sumField(products, 'quantity');
  const lead = [...products].sort((a, b) => Number(b.totalRevenue || 0) - Number(a.totalRevenue || 0))[0];
  const leadShare = sharePercent(lead?.totalRevenue, totalRevenue);
  const money = (amount) =>
    currencyUnavailable ? 'Rates unavailable' : formatMoney(amount, { currency: currencyCode });

  return [
    {
      title: 'Product Revenue',
      value: money(totalRevenue),
      change: '—',
      trend: 'neutral',
      context: 'Revenue across all products',
      featured: true,
    },
    neutralStat('Active Products', formatNumber(products.length), 'Products with sales in period'),
    {
      title: 'Top Product',
      value: money(lead?.totalRevenue || 0),
      change: leadShare == null ? '—' : formatPercent(leadShare),
      trend: 'neutral',
      context: truncateLabel(lead?.productName),
    },
    neutralStat('Units Sold', formatNumber(totalUnits), 'Quantity across product lines'),
  ];
}

export function buildEventSummaryStats(rows, comparison, { currencyCode, currencyUnavailable }) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const totalRevenue = sumField(rows, 'totalRevenue');
  const totalInvoices = sumField(rows, 'invoiceCount');
  const lead = rows[0];
  const leadShare = sharePercent(lead?.totalRevenue, totalRevenue);
  const money = (amount) =>
    currencyUnavailable ? 'Rates unavailable' : formatMoney(amount, { currency: currencyCode });

  const percentageChange = comparison?.percentageChange;
  const periodTrend =
    percentageChange == null ? 'neutral' : Number(percentageChange) >= 0 ? 'up' : 'down';
  const periodChange = percentageChange == null ? '—' : formatPercent(percentageChange);

  return [
    {
      title: 'Event Revenue',
      value: money(totalRevenue),
      change: periodChange,
      trend: periodTrend,
      context: 'Revenue from ranked events',
      featured: true,
    },
    neutralStat('Events Ranked', formatNumber(rows.length), 'Top events in this period'),
    {
      title: 'Top Event',
      value: money(lead?.totalRevenue || 0),
      change: leadShare == null ? '—' : formatPercent(leadShare),
      trend: 'neutral',
      context: truncateLabel(lead?.eventName),
    },
    neutralStat('Event Invoices', formatNumber(totalInvoices), 'Invoices linked to ranked events'),
  ];
}

export function buildVenueSummaryStats(venues, { currencyCode, currencyUnavailable }) {
  if (!Array.isArray(venues) || venues.length === 0) return [];

  const totalRevenue = sumField(venues, 'totalRevenue');
  const totalInvoices = sumField(venues, 'invoiceCount');
  const totalUnits = sumField(venues, 'quantity');
  const lead = venues[0];
  const leadShare = sharePercent(lead?.totalRevenue, totalRevenue);
  const money = (amount) =>
    currencyUnavailable ? 'Rates unavailable' : formatMoney(amount, { currency: currencyCode });

  return [
    {
      title: 'Venue Revenue',
      value: money(totalRevenue),
      change: '—',
      trend: 'neutral',
      context: 'Revenue across ranked venues',
      featured: true,
    },
    neutralStat('Venues Ranked', formatNumber(venues.length), 'Venues with revenue in period'),
    {
      title: 'Leading Venue',
      value: money(lead?.totalRevenue || 0),
      change: leadShare == null ? '—' : formatPercent(leadShare),
      trend: 'neutral',
      context: truncateLabel(lead?.venueName),
    },
    neutralStat('Venue Invoices', formatNumber(totalInvoices), `${formatNumber(totalUnits)} units sold`),
  ];
}
